import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  JournalEntryCard,
  JournalEntryDocChip,
  JournalEntryExternalLink,
  JournalEntryPostedBadge,
} from '@cottage/design-system/internal-tooling/ui'
import JournalEntryPreview from '@/components/JournalEntryPreview'
import WarningBanner from '@/components/WarningBanner'
import type { SelectOption } from '@/components/SearchableSelect'
import {
  postJournalEntry,
  updateJournalEntryLines,
  type JournalEntryLine,
} from '@/services/accounting'
import {
  generateFixedAssetDraft,
  pushFixedAssetDraftQboUpdate,
  type MonthDetailResponse,
} from '@/services/fixedAssets'
import { useFixedAssetsSyncStore } from '@/store/fixedAssetsSyncStore'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import {
  formatJePostedDate,
} from '../distribution-waterfall/waterfallJeStatus'
import {
  buildLineBadges,
  buildLineSections,
  orderLinesBySection,
  debitLinesMissingLocation,
  isZeroExpenseMonth,
  type AllocateSummary,
  type FaLine,
} from './fixedAssetsAllocateHelpers'

type Props = {
  monthDetail: MonthDetailResponse | null
  loading: boolean
  error: string | null
  canEdit: boolean
  summary: AllocateSummary
  accountOptions: SelectOption[]
  locationOptions: SelectOption[]
  onChanged: () => Promise<void>
}

function isLinesBalanced(lines: JournalEntryLine[]): boolean {
  const debits = lines
    .filter((line) => line.posting_type === 'Debit')
    .reduce((sum, line) => sum + Number(line.amount), 0)
  const credits = lines
    .filter((line) => line.posting_type === 'Credit')
    .reduce((sum, line) => sum + Number(line.amount), 0)
  return Math.abs(debits - credits) < 0.005
}

export default function FixedAssetsJeCard({
  monthDetail,
  loading,
  error,
  canEdit,
  summary,
  accountOptions,
  locationOptions,
  onChanged,
}: Props) {
  const [draftLines, setDraftLines] = useState<FaLine[]>([])
  const [actionLoading, setActionLoading] = useState(false)
  const [qboUpdateLoading, setQboUpdateLoading] = useState(false)
  const [posting, setPosting] = useState(false)
  const [postedMenuOpen, setPostedMenuOpen] = useState(false)
  const linesSaveGenerationRef = useRef(0)

  const draft = monthDetail?.draft ?? null
  const jeStatus = monthDetail?.status ?? 'none'
  const isPosted = jeStatus === 'posted' || draft?.status === 'posted'
  const isDraft = jeStatus === 'draft' && draft?.status === 'draft'
  const showDirty =
    Boolean(monthDetail?.content_dirty) ||
    Boolean(monthDetail?.needs_qbo_update) ||
    jeStatus === 'stale'
  const zeroExpense = monthDetail
    ? isZeroExpenseMonth(monthDetail.hint, summary)
    : false
  const balanced = useMemo(() => isLinesBalanced(draftLines), [draftLines])
  const missingLocations = useMemo(
    () => debitLinesMissingLocation(draftLines),
    [draftLines],
  )

  useEffect(() => {
    setDraftLines(orderLinesBySection((draft?.lines as FaLine[]) ?? []))
  }, [draft?.id, draft?.lines])

  const lineSections = useMemo(() => buildLineSections(draftLines), [draftLines])
  const lineBadges = useMemo(
    () => buildLineBadges(draftLines, monthDetail?.asset_breakdown ?? []),
    [draftLines, monthDetail?.asset_breakdown],
  )

  const canEditDraftLines = canEdit && isDraft && !posting
  const txnDateLabel = formatJePostedDate(draft?.txn_date ?? null)
  const docNumber = monthDetail?.doc_number || draft?.doc_number || null

  const handleLinesChange = useCallback(
    async (next: JournalEntryLine[]) => {
      setDraftLines(next as FaLine[])
      if (!draft?.id) return
      const saveGeneration = ++linesSaveGenerationRef.current
      try {
        const updated = await updateJournalEntryLines(draft.id, next)
        if (saveGeneration !== linesSaveGenerationRef.current) return
        setDraftLines(updated.lines as FaLine[])
      } catch (e) {
        if (saveGeneration !== linesSaveGenerationRef.current) return
        toast.error(formatApiError(e, 'Failed to save line changes'))
      }
    },
    [draft?.id],
  )

  const handleGenerate = async (options?: { overwritePosted?: boolean; isRegenerate?: boolean }) => {
    if (!canEdit || !monthDetail?.report_month) return
    const overwritePosted = options?.overwritePosted ?? false
    const isRegenerate = options?.isRegenerate ?? false
    if (overwritePosted) {
      const confirmed = window.confirm(
        docNumber
          ? `Overwrite ${docNumber} from the current asset register?`
          : 'Overwrite this journal entry from the current asset register?',
      )
      if (!confirmed) return
    }

    setActionLoading(true)
    try {
      const result = await generateFixedAssetDraft(monthDetail.report_month, {
        overwrite_posted: overwritePosted,
      })
      if (result.zero_expense && !result.draft) {
        toast.success('No depreciation or amortization this month.')
      } else {
        toast.success(
          overwritePosted
            ? 'Journal entry draft overwritten'
            : isRegenerate
              ? 'Journal entry draft updated'
              : 'Journal entry draft created',
        )
      }
      await onChanged()
    } catch (e) {
      toast.error(
        formatApiError(
          e,
          isRegenerate ? 'Failed to regenerate journal entry' : 'Failed to generate journal entry',
        ),
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleRegenerate = async () => {
    if (!canEdit || !monthDetail?.report_month || !draft) return
    const overwritePosted = jeStatus === 'posted' || jeStatus === 'stale'
    await handleGenerate({ overwritePosted, isRegenerate: true })
  }

  const handlePost = async () => {
    if (!canEdit || !draft || !balanced || missingLocations || showDirty) return

    const syncErrorCount = useFixedAssetsSyncStore.getState().errorCount
    const baseMessage = docNumber
      ? `Post ${docNumber} to QuickBooks?`
      : 'Post this journal entry to QuickBooks?'
    const confirmMessage =
      syncErrorCount > 0
        ? `${baseMessage}\n\nQuickBooks sync errors were found. Review the sync banner before posting. You can still post if you intend to proceed.`
        : baseMessage

    if (!window.confirm(confirmMessage)) return

    setPosting(true)
    try {
      await updateJournalEntryLines(draft.id, draftLines)
      await postJournalEntry(draft.id)
      toast.success('Journal entry posted to QuickBooks')
      await onChanged()
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to post journal entry'))
    } finally {
      setPosting(false)
    }
  }

  const handlePushQboUpdate = async () => {
    if (!canEdit || !draft) return
    const confirmed = window.confirm(
      docNumber
        ? `Update ${docNumber} in QuickBooks from the current asset register?`
        : 'Update this journal entry in QuickBooks from the current asset register?',
    )
    if (!confirmed) return

    setQboUpdateLoading(true)
    setPostedMenuOpen(false)
    try {
      const result = await pushFixedAssetDraftQboUpdate(draft.id, { force_regenerate: true })
      setDraftLines((result.draft.lines as FaLine[]) ?? [])
      toast.success(
        result.regenerated
          ? 'Regenerated draft and updated QuickBooks'
          : 'Updated journal entry in QuickBooks',
      )
      await onChanged()
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update journal entry in QuickBooks'))
    } finally {
      setQboUpdateLoading(false)
    }
  }

  const showGenerate =
    canEdit && jeStatus === 'none' && !zeroExpense && Math.abs(summary.grandTotal) > 0.005
  const showRegenerate = canEdit && Boolean(draft)
  const showPost = canEdit && isDraft && Boolean(draft) && !showDirty

  const showOutOfBalance = !zeroExpense && isDraft && !balanced
  const showMissingLocations = !zeroExpense && isDraft && missingLocations
  const alerts =
    !zeroExpense &&
    (draft?.warnings?.length ||
      draft?.error_message ||
      showOutOfBalance ||
      showMissingLocations ||
      showDirty)
      ? (
          <>
            {(draft?.warnings ?? []).map((warning, index) => (
              <WarningBanner key={index} message={warning} />
            ))}
            {draft?.error_message ? <WarningBanner message={draft.error_message} /> : null}
            {showOutOfBalance ? (
              <WarningBanner message="Journal entry is out of balance. Adjust lines before posting." />
            ) : null}
            {showMissingLocations ? (
              <WarningBanner message="Assign a location on every expense line before posting." />
            ) : null}
            {showDirty ? (
              <WarningBanner message="Out of date — regenerate before posting" />
            ) : null}
          </>
        )
      : undefined

  return (
    <JournalEntryCard
      posted={isPosted}
      identity={
        <>
          {docNumber ? <JournalEntryDocChip>{docNumber}</JournalEntryDocChip> : null}
          {txnDateLabel ? <span>{txnDateLabel}</span> : null}
          {isPosted && draft?.qbo_url ? (
            <JournalEntryExternalLink href={draft.qbo_url}>View in QuickBooks</JournalEntryExternalLink>
          ) : null}
        </>
      }
      actions={
        <>
          {showGenerate ? (
            <button
              type="button"
              className="btn btn-primary text-xs"
              disabled={actionLoading || qboUpdateLoading || loading}
              onClick={() => void handleGenerate()}
            >
              {actionLoading ? 'Creating…' : 'Create JE draft'}
            </button>
          ) : null}

          {showRegenerate ? (
            <button
              type="button"
              className="btn btn-primary text-xs"
              disabled={actionLoading || qboUpdateLoading || loading}
              onClick={() => void handleRegenerate()}
            >
              {actionLoading ? 'Regenerating…' : 'Regenerate'}
            </button>
          ) : null}

          {showPost ? (
            <button
              type="button"
              className="btn btn-primary text-xs"
              disabled={posting || actionLoading || !balanced || missingLocations}
              onClick={() => void handlePost()}
            >
              {posting ? 'Posting…' : 'Post to QuickBooks'}
            </button>
          ) : null}

          {isPosted ? (
            <JournalEntryPostedBadge
              menu={
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => void handlePushQboUpdate()}
                >
                  Update Existing JE
                </button>
              }
              menuOpen={postedMenuOpen}
              onMenuOpenChange={setPostedMenuOpen}
              loading={qboUpdateLoading}
            />
          ) : null}
        </>
      }
      alerts={alerts}
    >
      {error ? (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      {zeroExpense ? (
        <div className="py-3 text-sm text-gray-500">
          No depreciation or amortization this month.
        </div>
      ) : null}

      {!zeroExpense && draft && draftLines.length > 0 ? (
        <div className="max-h-[28rem] overflow-hidden">
          <JournalEntryPreview
            lines={draftLines}
            variant="split"
            hideHeader
            scrollable
            className="flex max-h-[24rem] min-h-0 flex-col"
            totalDebits={draft.total_debits}
            totalCredits={draft.total_credits}
            accountOptions={accountOptions}
            locationOptions={locationOptions}
            lineSections={lineSections}
            lineBadges={lineBadges}
            editableAmounts={canEditDraftLines}
            editableLines={canEditDraftLines}
            editableLocations={canEditDraftLines}
            locationRequiredOn="debits"
            onLinesChange={canEditDraftLines ? (next) => void handleLinesChange(next) : undefined}
          />
        </div>
      ) : null}

      {!zeroExpense && !loading && !draft && jeStatus === 'none' && Math.abs(summary.grandTotal) < 0.005 ? (
        <div className="py-3 text-sm text-gray-500">
          No depreciation or amortization this month.
        </div>
      ) : null}
    </JournalEntryCard>
  )
}
